import React, { useState } from "react";
import { Form, Input, Spin } from "antd";
import { UserOutlined, LockOutlined, LoadingOutlined } from "@ant-design/icons";
import { Link, Navigate, useNavigate } from "react-router-dom";
import logoPng from "../../assets/images/logo.png";
import dashboardGif from "../../assets/images/dashboard.gif";
import { useDispatch, useSelector } from "react-redux";
import LoginApi from "../../api/auth/LoginApi";
import { login, logout } from "../../features/AuthSlice";
import { firstLogin } from "../../features/FirstLoginSlice";
import { GetMeApi } from "../../api/users/GetMeApi";
import { userDetails } from "../../features/UserSlice";
import { openNotificationError } from "../../helpers/NotificationHelpers";
import { positionDetails } from "../../features/PositionSlice";

function Login() {
  const [submit, setSubmit] = useState(false);
  const authState = useSelector((state) => state.authState.value);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [positions, setPositions] = useState([]);

  if (authState === "login") {
    return <Navigate to={"/home"} replace />;
  }

  function showErrorMessage(message) {
    if (message === "Invalid credentials") {
      form.setFields([
        { name: "username", errors: [""] },
        { name: "password", errors: ["Username and password didn't match"] },
      ]);
    } else if (message === "User not found") {
      form.setFields([{ name: "username", errors: ["User not found"] }]);
    } else {
      form.setFields([
        { name: "username", errors: [""] },
        { name: "password", errors: ["Couldn't login. Try again later."] },
      ]);
    }
  }

  async function onFinish(values) {
    setSubmit(true);
    const apiResponse = await LoginApi(values.username, values.password);
    if (apiResponse.error === "Not Found") {
      showErrorMessage(apiResponse.message);
    } else if (apiResponse.error === "Bad Request") {
      showErrorMessage(apiResponse.message);
    } else if (apiResponse.data?.customerAccessToken) {
      localStorage.setItem("authToken", apiResponse.data?.customerAccessToken);
      localStorage.setItem("authUser", apiResponse.data?.username);
      getUserDetails();
      dispatch(login());
      if (apiResponse.data?.firstLogin) {
        dispatch(firstLogin(true));
        navigate("/setup-profile");
      } else if (!apiResponse.data?.firstLogin && positions.length === 0) {
        navigate("/not-found");
      } else {
        navigate("/home");
      }
    } else {
      showErrorMessage("Something is wrong");
    }
    setSubmit(false);
  }

  async function getUserDetails() {
    const apiResponse = await GetMeApi();
    if (apiResponse.error === "Unauthorized") {
      openNotificationError("bottoRight", apiResponse.message);
      localStorage.setItem("authToken", "");
      localStorage.setItem("authUser", "");
      dispatch(logout());
    } else if (apiResponse.success) {
      dispatch(userDetails(apiResponse.data));
      const lastPosition = localStorage.getItem("stPosition");
      if (lastPosition) {
        const position = apiResponse.data?.positions.find((pos) => pos.id === lastPosition);
        if (position) {
          dispatch(positionDetails(position));
          localStorage.setItem("currentPositionId", position.id);
        } else {
          dispatch(positionDetails(apiResponse.data?.positions[0]));
          localStorage.setItem("stPosition", apiResponse.data?.positions[0]?.id);
          localStorage.setItem("currentPositionId", apiResponse.data?.positions[0]?.id);
        }
      } else {
        dispatch(positionDetails(apiResponse.data?.positions[0]));
        localStorage.setItem("stPosition", apiResponse.data?.positions[0]?.id);
        localStorage.setItem("currentPositionId", apiResponse.data?.positions[0]?.id);
      }
      setPositions(apiResponse.data?.positions);
    } else {
      localStorage.setItem("authToken", "");
      localStorage.setItem("authUser", "");
      dispatch(logout());
    }
  }

  return (
    <>
      <style>{`
        .login-page {
          position: fixed;
          top: 0; left: 0; right: 0; bottom: 0;
          display: flex;
          flex-direction: row;
          margin: 0;
          padding: 0;
          z-index: 9999;
          overflow: hidden;
        }
        .login-left {
          flex: 0 0 45%;
          background-color: #FFF7F1;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          padding: 60px 48px;
        }
        .login-right {
          flex: 0 0 55%;
          background: linear-gradient(180deg, rgba(241, 90, 36, 0.2) 0%, rgba(255, 237, 230, 0.2) 100%);
          display: flex;
          align-items: center;
          justify-content: flex-start;
          overflow: visible;
          padding: 48px 0 48px 128px;
          position: relative;
        }
        .login-logo {
          width: 80px;
          height: 80px;
          object-fit: contain;
          margin-bottom: 40px;
          align-self: flex-start;
        }
        .login-heading {
          font-size: 30px;
          font-weight: 700;
          color: #1a1a1a;
          margin: 0 0 36px 0;
          line-height: 1.3;
          font-family: inherit;
          align-self: flex-start;
        }
        .login-sign-btn {
          width: 100%;
          height: 50px;
          background-color: #F15A24 !important;
          color: #ffffff !important;
          border: none !important;
          border-radius: 8px !important;
          font-size: 17px !important;
          font-weight: 600 !important;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          outline: none;
          box-shadow: none !important;
        }
        .login-sign-btn:hover {
          background-color: #d94e1f !important;
        }
        .login-forgot {
          font-size: 14px;
          color: #666;
          margin-top: 8px;
          align-self: flex-start;
        }
        .login-reset-link {
          color: #F15A24 !important;
          font-weight: 600;
          text-decoration: none;
        }
        .login-gif-wrap {
          position: relative;
          flex-shrink: 0;
          width: 145%;
          margin-right: -28%;
        }
        .login-gif {
          width: 150%;
          border-radius: 14px;
          box-shadow: 0 8px 40px rgba(241, 90, 36, 0.2);
          object-fit: contain;
          display: block;
        }
        .login-gif-fade {
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 32%;
          border-radius: 0 0 14px 14px;
          background: linear-gradient(to bottom, transparent 0%, #FFF7F1 100%);
          pointer-events: none;
        }
        .login-form-wrap {
          width: 100%;
          max-width: 420px;
        }
        .login-form-wrap .ant-form-item-label > label {
          font-size: 16px;
          color: #333;
          font-weight: 500;
        }
        .login-form-wrap .ant-input-affix-wrapper {
          height: 50px;
          border-radius: 8px;
          font-size: 15px !important;
          background-color: #FFF7F1 !important;
          border: 1px solid #e0d5ce !important;
          box-shadow: none !important;
        }
        .login-form-wrap .ant-input-affix-wrapper:hover {
          border-color: #F15A24 !important;
        }
        .login-form-wrap .ant-input-affix-wrapper:focus,
        .login-form-wrap .ant-input-affix-wrapper-focused {
          border-color: #F15A24 !important;
          box-shadow: 0 0 0 2px rgba(241,90,36,0.1) !important;
          background-color: #FFF7F1 !important;
        }
        .login-form-wrap .ant-input-affix-wrapper > input.ant-input,
        .login-form-wrap .ant-input-affix-wrapper input,
        .login-form-wrap .ant-input {
          background-color: #FFF7F1 !important;
          font-size: 15px !important;
          color: #1a1a1a !important;
        }
        .login-form-wrap input:-webkit-autofill,
        .login-form-wrap input:-webkit-autofill:hover,
        .login-form-wrap input:-webkit-autofill:focus,
        .login-form-wrap input:-webkit-autofill:active {
          -webkit-box-shadow: 0 0 0 50px #FFF7F1 inset !important;
          -webkit-text-fill-color: #1a1a1a !important;
          font-size: 15px !important;
          transition: background-color 5000s ease-in-out 0s;
        }
      `}</style>

      <div className="login-page">

        {/* LEFT */}
        <div className="login-left">
          <div style={{ width: "100%", maxWidth: "420px" }}>
            <img src={logoPng} alt="Strategizer" className="login-logo" />
            <h1 className="login-heading">Get Started with Strategizer</h1>

            <Form
              name="login"
              layout="vertical"
              form={form}
              onFinish={onFinish}
              requiredMark={false}
              className="login-form-wrap"
            >
              <Form.Item
                label="Username"
                name="username"
                rules={[{ required: true, message: "Please enter username!" }]}
                style={{ marginBottom: 20 }}
              >
                <Input
                  prefix={<UserOutlined style={{ color: "#ccc", fontSize: 16 }} />}
                  placeholder="Username"
                />
              </Form.Item>

              <Form.Item
                label="Password"
                name="password"
                rules={[{ required: true, message: "Please enter password!" }]}
                style={{ marginBottom: 28 }}
              >
                <Input.Password
                  prefix={<LockOutlined style={{ color: "#ccc", fontSize: 16 }} />}
                  placeholder="Password"
                />
              </Form.Item>

              <Form.Item style={{ marginBottom: 16 }}>
                <button type="submit" className="login-sign-btn">
                  {submit
                    ? <Spin size="small" indicator={<LoadingOutlined style={{ color: "#fff" }} spin />} />
                    : "Sign in"
                  }
                </button>
              </Form.Item>
            </Form>

            <p className="login-forgot">
              Forgot your password?{" "}
              <Link to="/forgot-password" className="login-reset-link">
                Reset Password
              </Link>
            </p>
          </div>
        </div>

        {/* RIGHT */}
        <div className="login-right">
          <div className="login-gif-wrap">
            <img src={dashboardGif} alt="Dashboard preview" className="login-gif" />
            <div className="login-gif-fade" />
          </div>
        </div>

      </div>
    </>
  );
}

export default Login;
